<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>

    <v-card>
      <v-card-title>Flow Exporters</v-card-title>
      <v-card-subtitle>
        Manage exporters used for network flow ingestion.
      </v-card-subtitle>

      <v-card-text class="pt-0 pb-2">
        <div class="font-weight-bold text-dark mb-1">Collector Ports</div>
        <div class="text-muted mb-3">
          Send flow records to the Collector Address shown for each exporter,
          using the port that matches the flow protocol you are exporting.
        </div>
        <v-row dense>
          <v-col
            v-for="port in collectorPorts"
            :key="port.protocol"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card outlined class="px-4 py-3">
              <div class="small text-uppercase font-weight-bold text-muted">
                {{ port.protocol }}
              </div>
              <div class="h5 mb-0 text-monospace font-weight-bold text-primary">
                {{ port.transport }}/{{ port.port }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="exporters"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1],
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="exporter_address"
        class="elevation-1"
      >
        <template v-slot:header.collector_address="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.collector_address }}</span>
          </v-tooltip>
        </template>
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Exporter</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-form ref="exporterForm" lazy-validation>
                    <v-container>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.exporter_address"
                            label="Exporter Address*"
                            :disabled="editedIndex > -1"
                            :rules="addressRules"
                            required
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="sampleRateInput"
                            type="number"
                            min="1"
                            label="Sample Rate"
                            :rules="sampleRateRules"
                            hint="Leave blank unless not included in flow records"
                            persistent-hint
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.sample_rate="{ item }">
          {{ item.sample_rate === null ? "—" : item.sample_rate }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
          <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import ipAddr from "ip-address";

export default {
  mounted() {
    this.loadData();
  },
  data: () => ({
    selected: [],
    headers: [
      { text: "Exporter Address", align: "left", value: "exporter_address" },
      { text: "Collector Address", value: "collector_address" },
      { text: "Sample Rate", value: "sample_rate" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    exporters: [],
    collectorPorts: [
      { protocol: "sFlow", transport: "UDP", port: 6343 },
      { protocol: "IPFIX/NetFlow", transport: "UDP", port: 2055 },
    ],
    errors: [],
    loading: true,
    dialog: false,
    editedIndex: -1,
    editedItem: {
      exporter_address: "",
      sample_rate: null,
    },
    defaultItem: {
      exporter_address: "",
      sample_rate: null,
    },
    search: "",
    sampleRateInput: "",
    headerHints: {
      collector_address:
        "Address of the collector that the exporter must send its flow records to",
    },
    addressRules: [
      (v) => !!v || "Address is required",
      (v) =>
        (!!v &&
          (new ipAddr.Address4(v).isValid() ||
            new ipAddr.Address6(v).isValid())) ||
        "Address is invalid",
    ],
    sampleRateRules: [
      (v) => v === "" || /^[0-9]+$/.test(String(v)) || "Must be a whole number",
      (v) => v === "" || parseInt(v, 10) > 0 || "Must be greater than 0",
    ],
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Exporter" : "Edit Exporter";
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
  },
  methods: {
    loadData() {
      this.loading = true;
      api
        .query("/v2/exporters")
        .then((response) => {
          this.exporters = response.data.exporters || [];
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => (this.loading = false));
    },
    editItem(item) {
      this.editedIndex = this.exporters.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.sampleRateInput =
        item.sample_rate === null || item.sample_rate === undefined
          ? ""
          : String(item.sample_rate);
      this.dialog = true;
    },
    deleteItem(item) {
      if (!confirm(`Are you sure you want to delete exporter ${item.exporter_address}?`)) {
        return;
      }

      api
        .delete(`/v2/exporters/${item.exporter_address}`)
        .then(() => {
          const index = this.exporters.indexOf(item);
          if (index > -1) this.exporters.splice(index, 1);
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.sampleRateInput = "";
        this.editedIndex = -1;
      }, 300);
    },
    save() {
      if (!this.$refs.exporterForm.validate()) return;

      const sampleRate =
        this.sampleRateInput === "" ? null : parseInt(this.sampleRateInput, 10);

      if (this.editedIndex > -1) {
        api
          .patch(`/v2/exporters/${this.editedItem.exporter_address}`, {
            sample_rate: sampleRate,
          })
          .then((response) => {
            Object.assign(
              this.exporters.find(
                (exporter) =>
                  exporter.exporter_address === response.data.exporter_address
              ),
              response.data
            );
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      } else {
        api
          .post("/v2/exporters", {
            exporter_address: this.editedItem.exporter_address,
            sample_rate: sampleRate,
          })
          .then((response) => {
            this.exporters.push(response.data);
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      }

      this.close();
    },
  },
};
</script>
